import { useRef, useState } from "react";
import { Printer, Send, FileDown, Eraser, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { COMPANY } from "@/lib/content";
import { http, errText } from "@/lib/api";
import { SECTIONS } from "./sections";
import { SectionBody } from "./FormFields";
import { SectionHeader } from "./Fields";
import { StepRail, stepOfError } from "./StepRail";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { collect, validate, checkResume, checkCerts, checkDocs } from "./formUtils";
import { StatusDialog } from "./StatusDialog";

const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}-${Math.random().toString(36).slice(2, 12)}`);

const officeStatus = (data, email) => (data?.office_email === "sent"
  ? { email: "ok", emailMsg: `Your complete application${data?.duplicate ? " (already on file)" : ""} and uploaded documents were accepted for delivery to ${email}.` }
  : { email: "fail", emailMsg: "Your application is safely saved, but the email to our hiring inbox hasn't gone through yet. We retry automatically — you don't need to submit again. You can also tap Retry delivery." });

export const ApplicationForm = ({ blank = false }) => {
  const formRef = useRef(null);
  const lastHash = useRef("");
  const subId = useRef(newId());
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [summary, setSummary] = useState("");
  const [status, setStatus] = useState({ portal: "pending", email: "pending" });
  const [resumeNote, setResumeNote] = useState("");
  const [certNames, setCertNames] = useState([]);
  const [docNames, setDocNames] = useState([]);
  const [step, setStepRaw] = useState(0);
  const [done, setDone] = useState(() => new Set());
  const wizard = !blank;
  const goTo = (i) => {
    setStepRaw(i);
    const el = formRef.current;
    if (el) (window.__lenis ? window.__lenis.scrollTo(el, { offset: -140 }) : el.scrollIntoView({ behavior: "smooth" }));
  };
  const next = () => {
    const errs = validate(collect(formRef.current));
    const mine = Object.fromEntries(Object.entries(errs).filter(([k]) => stepOfError(k) === step));
    setErrors((p) => ({ ...Object.fromEntries(Object.entries(p).filter(([k]) => stepOfError(k) !== step)), ...mine }));
    if (Object.keys(mine).length) { toast.error("Please complete the required fields in this section."); return; }
    setDone((p) => new Set(p).add(step));
    goTo(Math.min(step + 1, SECTIONS.length - 1));
  };
  const errorSteps = new Set(Object.keys(errors).filter((k) => errors[k]).map(stepOfError));

  const submit = async (e) => {
    e?.preventDefault();
    if (busy) return;
    const d = collect(formRef.current);
    if (d.honey) return;
    const errs = validate(d);
    setErrors(errs);
    if (Object.keys(errs).length) {
      const first = Math.min(...Object.keys(errs).map(stepOfError).filter((i) => i >= 0));
      if (wizard && Number.isFinite(first)) goTo(first);
      setTimeout(() => document.getElementById("appValidationErrorBox")?.focus(), 30);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    const hash = `${d.summary_text}|${d.resume?.name || ""}|${d.resume?.size || 0}|${[...d.certificates, ...d.documents].map((c) => c.name + c.size).join(",")}`;
    if (hash === lastHash.current) { setOpen(true); toast.info("This exact application was already submitted."); return; }
    if (lastHash.current && hash !== lastHash.current) subId.current = newId();
    setSummary(d.summary_text);
    setBusy(true);
    setStatus({ portal: "pending", email: "pending" });
    setOpen(true);
    const fd = new FormData();
    const { resume, certificates, documents, ...rest } = d;
    fd.append("payload", JSON.stringify({ ...rest, submission_id: subId.current }));
    if (resume) fd.append("resume", resume, resume.name);
    certificates.forEach((c) => fd.append("certificates", c, c.name));
    documents.forEach((c) => fd.append("documents", c, c.name));
    try {
      const { data } = await http.post("/applications", fd, { timeout: 120000 });
      lastHash.current = hash;
      setStatus({
        portal: "ok",
        portalMsg: `Reference ${data?.reference || ""}.${data?.duplicate ? " We already had this application on file, so no duplicate was created." : ""}${data?.confirmation_email === "sent" ? ` A confirmation email with your reference number was sent to ${d.email}.` : " Please note your reference number."}`,
        ...officeStatus(data, COMPANY.email),
      });
      if (data?.office_email === "sent") toast.success("Application submitted.");
    } catch (err) {
      setStatus({ portal: "fail", portalMsg: errText(err), email: "skipped", emailMsg: "Not attempted because the application wasn't saved." });
    } finally { setBusy(false); }
  };

  const retry = async () => {
    if (status.portal !== "ok") { lastHash.current = ""; submit(); return; }
    setStatus((s) => ({ ...s, email: "pending", emailMsg: "" }));
    try {
      const { data } = await http.post("/applications/notify-retry", { submission_id: subId.current }, { timeout: 120000 });
      setStatus((s) => ({ ...s, ...officeStatus(data, COMPANY.email) }));
    } catch (err) {
      setStatus((s) => ({ ...s, email: "fail", emailMsg: `${errText(err)} Your application is still saved — we'll keep retrying automatically.` }));
    }
  };
  const print = () => { setOpen(false); setTimeout(() => window.print(), 250); };
  const clearAll = () => { formRef.current?.reset(); setErrors({}); setResumeNote(""); setCertNames([]); setDocNames([]); setDone(new Set()); setStepRaw(0); lastHash.current = ""; subId.current = newId(); toast("All entries cleared."); };
  const onResume = (e) => {
    const f = e.target.files?.[0];
    const err = checkResume(f);
    setErrors((p) => ({ ...p, resume: err || undefined }));
    setResumeNote(f && !err ? `Ready: ${f.name} (${Math.max(1, Math.round(f.size / 1024))} KB)` : "");
  };
  const onCerts = (e) => {
    const files = Array.from(e.target.files || []);
    const err = checkCerts(files);
    setErrors((p) => ({ ...p, certificates: err || undefined }));
    setCertNames(err ? [] : files.map((f) => `${f.name} (${Math.max(1, Math.round(f.size / 1024))} KB)`));
  };
  const onDocs = (e) => {
    const files = Array.from(e.target.files || []);
    const err = checkDocs(files);
    setErrors((p) => ({ ...p, documents: err || undefined }));
    setDocNames(err ? [] : files.map((f) => `${f.name} (${Math.max(1, Math.round(f.size / 1024))} KB)`));
  };
  const errList = Object.values(errors).filter(Boolean);

  return (
    <form ref={formRef} onSubmit={submit} noValidate className={`p-6 sm:p-10 ${wizard ? "lg:grid lg:grid-cols-[220px_1fr] lg:gap-10" : ""}`} data-testid="application-form" aria-describedby="app-instructions">
      {wizard && <StepRail step={step} setStep={goTo} done={done} errorSteps={errorSteps} />}
      <div className="space-y-12 min-w-0 pt-6 lg:pt-0">
      <input type="text" name="_honey" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
      {errList.length > 0 && (
        <div id="appValidationErrorBox" tabIndex={-1} role="alert" className="p-4 rounded-2xl bg-red-50 border-2 border-red-700 text-red-900 text-sm no-print" data-testid="application-error-box">
          <p className="font-bold">Please review the following before submitting:</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">{errList.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}
      {SECTIONS.map((s, i) => (
        <fieldset key={s.id} className={`print-avoid ${wizard && i !== step ? "hidden print:block" : ""}`} data-testid={`application-section-${s.id}`}>
          <SectionHeader s={s} />
          {s.note && <p className="text-sm text-[#2C3B37] leading-relaxed mb-4">{s.note}</p>}
          <SectionBody s={s} errors={errors} />
          {s.resume && (
            <div className="mt-5 p-5 rounded-2xl bg-[#EDF1E6] border border-[#154F49]/20 no-print">
              <label htmlFor="app_resume" className="block text-sm font-bold text-[#154F49] mb-1">Attach resume (optional — PDF, DOC, or DOCX, max 5 MB)</label>
              <input id="app_resume" type="file" name="resume" onChange={onResume} accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" data-testid="field-resume" aria-invalid={errors.resume ? true : undefined} aria-describedby="app_resume_help"
                className="block w-full text-sm text-[#154F49] file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:font-bold file:bg-[#154F49] file:text-[#FBF8F1] bg-white rounded-xl border border-[#154F49]/30 p-2 cursor-pointer" />
              <p id="app_resume_help" className={`text-xs mt-1 ${errors.resume ? "font-bold text-red-800" : "text-[#465F57]"}`} data-testid="resume-feedback">
                {errors.resume || resumeNote || "Accepted: .pdf, .doc, .docx up to 5 MB. Do not upload Social Security cards, passports, or medical records."}
              </p>
              <div className="mt-5 pt-5 border-t border-[#154F49]/15">
                <label htmlFor="app_certificates" className="block text-sm font-bold text-[#154F49] mb-1">Attach certificates (optional — CPR, First Aid, nursing license, other credentials)</label>
                <input id="app_certificates" type="file" name="certificates" multiple onChange={onCerts} accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" data-testid="field-certificates" aria-invalid={errors.certificates ? true : undefined} aria-describedby="app_cert_help"
                  className="block w-full text-sm text-[#154F49] file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:font-bold file:bg-[#CDA34F] file:text-[#154F49] bg-white rounded-xl border border-[#154F49]/30 p-2 cursor-pointer" />
                <p id="app_cert_help" className={`text-xs mt-1 ${errors.certificates ? "font-bold text-red-800" : "text-[#465F57]"}`} data-testid="certificates-feedback">
                  {errors.certificates || "Up to 5 files · PDF, JPG or PNG · 5 MB each. Photos of certificates are fine. Do not upload ID cards, Social Security cards or medical records."}
                </p>
                {certNames.length > 0 && !errors.certificates && (
                  <ul className="mt-2 space-y-1 text-xs font-semibold text-[#154F49]" data-testid="certificates-list">{certNames.map((n) => <li key={n}>✓ {n}</li>)}</ul>
                )}
              </div>
              <div className="mt-5 pt-5 border-t border-[#154F49]/15">
                <label htmlFor="app_documents" className="block text-sm font-bold text-[#154F49] mb-1">Other supporting documents (optional — cover letter, reference letters)</label>
                <input id="app_documents" type="file" name="documents" multiple onChange={onDocs} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png" data-testid="field-documents" aria-invalid={errors.documents ? true : undefined} aria-describedby="app_docs_help"
                  className="block w-full text-sm text-[#154F49] file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:font-bold file:bg-[#154F49] file:text-[#FBF8F1] bg-white rounded-xl border border-[#154F49]/30 p-2 cursor-pointer" />
                <p id="app_docs_help" className={`text-xs mt-1 ${errors.documents ? "font-bold text-red-800" : "text-[#465F57]"}`} data-testid="documents-feedback">
                  {errors.documents || "Up to 3 files · PDF, DOC, DOCX, JPG or PNG · 5 MB each. Do not upload passports, Social Security cards or medical records."}
                </p>
                {docNames.length > 0 && !errors.documents && (
                  <ul className="mt-2 space-y-1 text-xs font-semibold text-[#154F49]" data-testid="documents-list">{docNames.map((n) => <li key={n}>✓ {n}</li>)}</ul>
                )}
              </div>
            </div>
          )}
          {s.certification && (
            <label htmlFor="app_consent" className="mt-5 flex items-start gap-3 p-5 rounded-2xl border border-[#154F49]/20 bg-white cursor-pointer no-print">
              <input id="app_consent" type="checkbox" name="consent" value="Agreed" className="mt-1 w-5 h-5 accent-[#154F49] shrink-0" data-testid="field-consent" aria-invalid={errors.consent ? true : undefined} />
              <span className="text-sm text-[#1A2E2B] leading-relaxed">
                <strong className="text-[#154F49]">Consent to online transmission<span className="text-[#9B742A]"> *</span>:</strong> I consent to Elevion Care Network LLC receiving the information in this application and any attached resume for employment review. It will be sent over HTTPS and stored in Elevion's private hiring portal, and a copy is emailed to {COMPANY.email} through the FormSubmit email service. I understand Social Security, driver's license and work permit numbers are not requested here and will be collected securely by HR during onboarding. I will not upload images of identity documents or medical records here.
              </span>
            </label>
          )}
        </fieldset>
      ))}
      {wizard && step < SECTIONS.length - 1 && (
        <div className="flex items-center justify-between gap-3 pt-2 no-print" data-testid="step-nav">
          <button type="button" onClick={() => goTo(Math.max(step - 1, 0))} disabled={step === 0} className="btn-ghost disabled:opacity-40" data-testid="step-back-btn"><ArrowLeft className="w-4 h-4" /> Back</button>
          <span className="hidden sm:block text-xs text-[#465F57]">Next: {SECTIONS[step + 1].title}</span>
          <button type="button" onClick={next} className="btn-primary px-6" data-testid="step-next-btn">Save &amp; continue <ArrowRight className="w-4 h-4" /></button>
        </div>
      )}
      {!blank && step === SECTIONS.length - 1 && (
        <div className="pt-6 border-t border-[#154F49]/15 flex flex-col lg:flex-row gap-5 lg:items-center justify-between no-print">
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={() => goTo(step - 1)} className="btn-ghost px-5 py-4" data-testid="step-back-final-btn"><ArrowLeft className="w-4 h-4" /> Back</button>
            <button type="submit" disabled={busy} className="btn-primary px-7 py-4 disabled:opacity-60" data-testid="application-submit-button"><Send className="w-4 h-4" /> {busy ? "Submitting…" : "Submit Application Online"}</button>
            <button type="button" onClick={print} className="btn-gold px-6 py-4" data-testid="application-print-button"><Printer className="w-4 h-4" /> Print / Save as PDF</button>
            <a href="/application/blank" target="_blank" rel="noopener noreferrer" className="btn-ghost px-6 py-4" data-testid="application-blank-button"><FileDown className="w-4 h-4" /> Blank printable form</a>
            <button type="button" onClick={() => setConfirmClear(true)} className="btn-danger px-6 py-4" data-testid="application-clear-button"><Eraser className="w-4 h-4" /> Clear all entries</button>
          </div>
          <p className="text-xs text-[#465F57] max-w-xs flex gap-2"><ShieldCheck className="w-4 h-4 shrink-0 text-[#154F49]" /> Entries stay in this tab until you submit — nothing is saved in your browser.</p>
        </div>
      )}
      <StatusDialog open={open} onOpenChange={setOpen} status={status} summary={summary} onRetry={retry} onPrint={print} />
      </div>
      <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
        <AlertDialogContent className="bg-[#FBF8F1] border-2 border-[#154F49] rounded-3xl" data-testid="clear-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#154F49]">Clear all application entries?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#465F57]">This erases everything you've entered in all 10 sections. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clear-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={clearAll} className="bg-red-700 hover:bg-red-800" data-testid="clear-confirm-btn">Yes, clear all entries</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
};
